import type { MapEngine } from '@atlasguess/engine';
import type { LayerStateConfig } from '../config';

/**
 * Apply a mode's LayerStateConfig to an engine. Returns a cleanup function
 * that restores the engine to a sensible default state — call it on mode
 * unmount so the next mode (or the home view) starts from a clean baseline.
 *
 * "Clean baseline" = `compound`-style: texture on, borders on, fills off,
 * hover off. Same defaults the engine settles to after loadBorders.
 *
 * **Ordering gotcha:** `engine.loadBorders()` resets fills visibility to
 * `false` after building the mesh (so the demo doesn't get washed-out
 * tints when the user toggles borders alone). Modes that turn fills on
 * MUST call `applyEngineConfig` AFTER awaiting `loadBorders`, otherwise
 * the load will clobber the fills setting. City-distance modes don't
 * call loadBorders at all, so order doesn't matter for them.
 */
export function applyEngineConfig(engine: MapEngine, config: LayerStateConfig): () => void {
  // Apply target state. setOpacity tweens by default — we want the
  // transition to feel intentional when navigating between modes.
  engine.layers.texture.setOpacity(config.texture.opacity);
  engine.layers.texture.setVisible(config.texture.visible);

  engine.layers.outline.setBordersOpacity(config.borders.opacity);
  engine.layers.outline.setBordersVisible(config.borders.visible);

  engine.layers.outline.setFillOpacity(config.fills.opacity);
  engine.layers.outline.setFillsVisible(config.fills.visible);

  engine.layers.outline.setHoverEnabled(config.hover);

  return () => {
    engine.layers.texture.setOpacity(1);
    engine.layers.texture.setVisible(true);
    engine.layers.outline.setBordersOpacity(1);
    engine.layers.outline.setBordersVisible(true);
    engine.layers.outline.setFillOpacity(0.55);
    engine.layers.outline.setFillsVisible(false);
    engine.layers.outline.setHoverEnabled(false);
  };
}
